import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton } from 'Core/components/Button';
import { AlertError } from 'Core/components/Alert';
import Loader from 'Core/components/Loader';
import { messageOf } from 'Modules/checkout/api';
import MethodMarks from 'Modules/checkout/components/MethodMarks';

const OPTIONS = {
  'FINANCING/EASY_CREDIT': {
    title: 'Ratenkauf mit easyCredit',
    description: 'Finanzierung in monatlichen Raten',
  },
  'CARD/STRIPE': { title: 'Kreditkarte', description: 'Mit Kredit- oder Debitkarte bezahlen' },
  'SEPA/STRIPE': {
    title: 'SEPA-Lastschrift',
    description: 'Bequem vom Bankkonto einziehen lassen',
  },
  'INVOICE/MANUAL': { title: 'Rechnung', description: 'Überweisung nach Erhalt der Rechnung' },
};

const ORDER = Object.keys(OPTIONS);

export const keyOf = (row) => `${row.method}/${row.provider}`;

export default function SelectPaymentMethod({ options, selected, mutation }) {
  const { t } = useTranslation();
  const selectable = options
    .filter((row) => OPTIONS[keyOf(row)])
    .sort((a, b) => ORDER.indexOf(keyOf(a)) - ORDER.indexOf(keyOf(b)));
  const [choice, setChoice] = useState(selected ? keyOf(selected) : null);

  if (mutation.isLoading) {
    return <Loader message={t('checkout.saving', 'Zahlungsart wird gespeichert …')} />;
  }

  const submit = () => {
    const row = selectable.find((candidate) => keyOf(candidate) === choice);
    mutation.mutate({ method: row.method, provider: row.provider, intent: row.intent });
  };

  return (
    <Card className="border-solid border-gray-300 shadow-md !px-4 md:!px-6" cardPadding="sm">
      <h3 className="text-lg font-bold text-gray-900 mb-1 mt-2">
        {t('checkout.selectMethod.title', 'Wie möchten Sie bezahlen?')}
      </h3>
      <p className="text-sm text-gray-600 mb-3">
        {t('checkout.selectMethod.subtitle', 'Wählen Sie eine Zahlungsart, um fortzufahren.')}
      </p>
      {mutation.isError && <AlertError>{messageOf(mutation.error)}</AlertError>}
      <div className="flex flex-col gap-2" role="radiogroup">
        {selectable.map((row) => {
          const key = keyOf(row);
          const option = OPTIONS[key];
          const chosen = key === choice;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={chosen}
              key={key}
              data-testid={`method-${key}`}
              onClick={() => setChoice(key)}
              className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-left border rounded-lg px-3 py-2.5 md:px-4 transition ${
                chosen
                  ? 'border-primary ring-2 ring-primary/30 bg-blue-50/40'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <span>
                <span className="block font-semibold text-gray-900">
                  {t(`checkout.method.${key}.title`, option.title)}
                </span>
                <span className="block text-sm text-gray-600">
                  {t(`checkout.method.${key}.description`, option.description)}
                </span>
              </span>
              <MethodMarks methodKey={key} />
            </button>
          );
        })}
      </div>
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 mb-1">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" data-testid="secure-note">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          {t('checkout.secure', 'Sichere, verschlüsselte Zahlung')}
        </span>
        <PrimaryButton
          type="button"
          data-testid="continue"
          className="w-full sm:w-auto"
          disabled={!choice}
          onClick={submit}
        >
          {t('common.continue', 'Weiter')}
        </PrimaryButton>
      </div>
    </Card>
  );
}
